import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import LoadingSpinner from "../../Components/LoadingSpinner";
import UnPaidInvoice from "./UnPaidInvoice";
import { useInvoices } from "../../../Providers/InvoicesProvider";
import { useParams } from "../../../Providers/ParamsProvider";
import { Licenses } from "../../../Data/Licenses.ts";
import UnPaidInvoicesFilter from "./Components/UnPaidInvoicesFilter.jsx";

const UnPaidInvoices = ({ invoices, isLoading, currentView, setShowNoEInvoiceModal }) => {
    const { t } = useTranslation();
    const { handleGetUnPaid } = useInvoices()
    const [selectedSeller, setSelectedSeller] = useState('');
    const [selectedDocType, setSelectedDocType] = useState('');
    const [dateFrom, setDateFrom] = useState('')
    const [dateTo, setDateTo] = useState('')
    const dateFromRef = useRef(null)
    const dateToRef = useRef(null)
    const [selectedOrder, setSelectedOrder] = useState('');
    const { isMobile, license } = useParams();

    // Prefiksy dla różnych typów dokumentów
    const invoicePrefixes = ['F', 'FV', 'S', 'SD', 'E', 'EU', 'EP'];
    const correctionPrefixes = ['V', 'W', 'WD', 'N', 'NN', 'EC', 'EK', 'EV'];

    // Funkcja do filtrowania listy dokumentów    
    const filteredInvoices = invoices && invoices.length > 0 ? invoices.filter(invoice => {
        const matchesSeller = selectedSeller ? invoice.seller === selectedSeller : true;

        const matchesDocType = (() => {
            if (!selectedDocType || selectedDocType === 'Wszystkie') return true; // Wszystkie
            if (selectedDocType === 'Faktury') {
                return invoicePrefixes.some(prefix => invoice.docNumber.startsWith(prefix));
            }
            if (selectedDocType === 'Korekty') {
                return correctionPrefixes.some(prefix => invoice.docNumber.startsWith(prefix));
            }
            return false;
        })();
        return matchesSeller && matchesDocType;
    }) : [];

    //Funkcja do sortowania listy dokumentów
    const sortedInvoices = [...filteredInvoices].sort((a, b) => {
        switch (selectedOrder) {
            case "newest":
                return new Date(b.docCreated) - new Date(a.docCreated); // Sortowanie od najnowszych
            case "oldest":
                return new Date(a.docCreated) - new Date(b.docCreated); // Sortowanie od najstarszych
            case "docAsc":
                return a.docNumber.localeCompare(b.docNumber); // Numer dokumentu rosnąco
            case "docDesc":
                return b.docNumber.localeCompare(a.docNumber); // Numer dokumentu malejąco
            case "valueAsc":
                return a.value - b.value; // Wartość dokumentu rosnąco
            case "valueDesc":
                return b.value - a.value; // Wartość dokumentu malejąco
            case "paidAsc":
                return a.paidValue - b.paidValue; // Wpłacono rosnąco
            case "paidDesc":
                return b.paidValue - a.paidValue; // Wpłacono malejąco
            case "timeToPayDesc":
                return new Date(b.dateToPay) - new Date(a.dateToPay); // Termin płątności rosnąco
            case "timeToPayAsc":
                return new Date(a.dateToPay) - new Date(b.dateToPay); // Termin płatności malejąco
            default:
                return 0;
        }
    });

    useEffect(() => {
        setSelectedDocType('')
        setSelectedSeller('')
        setSelectedOrder('timeToPayAsc')
    }, [currentView])

    useEffect(() => {
        handleGetUnPaid(dateFrom, dateTo);
    }, [dateFrom, dateTo])

    return (
        <div>
            <div className="row justify-content-center">
                <div className="justify-content-center">
                    {/* Sekcja filtrów */}
                    <UnPaidInvoicesFilter
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        setDateTo={setDateTo}
                        setDateFrom={setDateFrom}
                        dateFromRef={dateFromRef}
                        dateToRef={dateToRef}
                        setSelectedDocType={setSelectedDocType}
                        selectedSeller={selectedSeller}
                        setSelectedSeller={setSelectedSeller}
                        setSelectedOrder={setSelectedOrder}
                        invoices={invoices}
                        selectedDocType={selectedDocType}
                        currentView={currentView}
                    />
                    <div className="table-responsive">
                        <table className={`${isMobile ? 'table-font-small' : ''} table table-bordered`}>
                            <thead>
                                <tr>
                                    <th style={{ alignContent: 'start', verticalAlign: 'top', width: '10%', fontSize: '14px' }}>{t("pay_term_table_header").toLocaleUpperCase()}</th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "20%", fontSize: '14px' }}>
                                        {license == Licenses.TgInstalacje ? t("seller_table_header_orders_page_TG").toLocaleUpperCase() : t("seller_table_header_orders_page").toLocaleUpperCase()}                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px' }}>
                                        {t("dok_name_table_header_unpaid_page").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px' }}>
                                        {t("nr_dok_table_header_unpaid_page").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px' }}>
                                        {t("date_dok_table_header_unpaid_page").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px', textAlign: 'right' }}>
                                        {t("value_table_header_unpaid_page").toLocaleUpperCase()}
                                        <br />
                                        <span style={{ fontWeight: 'normal' }}>{t("value_paid").toLocaleUpperCase()}</span>
                                    </th>
                                    <th style={{ alignContent: "start", verticalAlign: 'top', width: "10%", fontSize: '14px', textAlign: 'right' }}>
                                        {t("value_left_to_pay").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ width: 'auto', verticalAlign: 'top', fontSize: '14px' }}>
                                        {t("comments").toLocaleUpperCase()}
                                    </th>
                                    <th style={{ width: '3%' }}></th>
                                    <th style={{ width: '3%' }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedInvoices &&
                                    sortedInvoices.map((invoice, index) => {
                                        return <UnPaidInvoice key={index} invoice={invoice} setShowNoEInvoiceModal={setShowNoEInvoiceModal} />;
                                    })}
                            </tbody>
                        </table>
                        {isLoading && <LoadingSpinner />}
                        {!isLoading && sortedInvoices && sortedInvoices.length === 0 && sortedInvoices && (
                            <div style={{ textAlign: "center" }}>
                                {t("message_norecords_table_invoices_page")}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UnPaidInvoices;
